import { getAdder, getUnitStart, updateDateByPos, roundTimeByStep, } from "../time";
import { format, differenceInCalendarDays, isSameDay, addMinutes, differenceInMinutes, areIntervalsOverlapping, endOfDay, getHours, getMinutes, startOfDay, endOfHour, startOfHour, } from "date-fns";
import { isEventInTimeRange, isMultiEvent, stackEvents, roundEvents, } from "../events";
import { uid } from "../helpers";
import { locateID } from "@xbs/lib-dom";
import { EventStore } from "./eventStore";
const addDays = getAdder("day");
const addWeeks = getAdder("week");
function nextOverlapedEvent(event, events, stack) {
    events = events.sort((a, b) => stack[a.id] - stack[b.id]);
    return events.find(ev => stack[ev.id] > stack[event.id] &&
        areIntervalsOverlapping({
            start: ev.start_date,
            end: ev.end_date,
        }, {
            start: event.start_date,
            end: event.end_date,
        }));
}
class WeekView {
    constructor() {
        this.getBounds = (date, config) => {
            const start = getUnitStart("week", date, config?.weekStartsOn);
            const end = endOfDay(addDays(start, 6));
            return [start, end];
        };
        this.getNext = (date) => {
            return addWeeks(date, 1);
        };
        this.getPrev = (date) => {
            return addWeeks(date, -1);
        };
        this.getTitle = (date, config) => {
            const [start, end] = this.getBounds(date, this._config);
            const s = format(start, "d LLL y", { locale: config.locale });
            const e = format(end, "d LLL y", { locale: config.locale });
            return `${s} - ${e}`;
        };
        this.calculateTop = (date, scaleModel) => {
            const minutePart = date.getHours() * 60;
            const timeScale = scaleModel.timeScale;
            const minutes = date.getMinutes();
            const cellHeight = 100 / timeScale.length; // 100% / length
            const minuteHeight = cellHeight / 60; // height%  / 60 minutes
            const timeScaleIndex = timeScale.findIndex((scale) => scale.value === minutePart);
            const top = cellHeight * timeScaleIndex + minutes * minuteHeight;
            return top;
        };
        this.calculateEventPosition = (event, scaleModel) => {
            let top = this.calculateTop(event.start_date, scaleModel);
            top = top < 0 ? 0 : top;
            const bottom = this.calculateTop(event.end_date, scaleModel);
            const height = (bottom <= 0 || bottom < top ? 100 : bottom) - top + "%";
            const width = `90%`;
            top = top + "%";
            return {
                top,
                height,
                width,
            };
        };
        this.multieventPosition = (event, scaleModel, crossedEvents) => {
            const { dateScale } = scaleModel;
            const startIndex = dateScale.findIndex(date => isSameDay(date.value, event.start_date));
            const endIndex = dateScale.findIndex(date => isSameDay(date.value, event.end_date));
            const length = Object.keys(dateScale).length;
            let left = 0; // initial in %
            let width = 100; // initial width in % for multievent
            if (startIndex > 0) {
                left = (startIndex * 100) / length;
            }
            width -= left;
            if (endIndex > 0) {
                const isStartOfTheDay = isSameDay(event.end_date, addMinutes(event.end_date, -1));
                const colIndex = endIndex + (isStartOfTheDay ? 1 : 0);
                width -= 100 - (colIndex * 100) / length;
            }
            if (!endIndex) {
                width = width / length;
            }
            width = `calc(${width}% - 2px)`;
            left = left + "%";
            const index = crossedEvents[event.id];
            let top = index
                ? index * (this._config.eventHeight + 5)
                : 0;
            top = top + "px";
            return { left, width, height: this._config.eventHeight + "px", top };
        };
        this.getIndayEventsModel = (events, scaleModel, config) => {
            const res = [];
            const filteredByDay = [];
            this._scaleModel.dateScale.forEach((date) => {
                const eventsByDate = events.filter(ev => isSameDay(ev.start_date, date.value));
                filteredByDay.push(eventsByDate);
            });
            const { eventMargin, columnPadding } = this._config;
            filteredByDay.forEach(eventsArr => {
                const stack = stackEvents(eventsArr);
                const maxOverlapCounts = Math.max(...Object.keys(stack).map(key => stack[key]));
                const colWidth = `(100% - ${columnPadding})`;
                const sectionWidth = `${colWidth}/${maxOverlapCounts + 1}`;
                eventsArr.forEach(event => {
                    const position = this.calculateEventPosition(event, scaleModel);
                    const index = stack[event.id];
                    if (config.overlay) {
                        position.left = `calc(${eventMargin} * ${index})`;
                        position.width = `calc(${colWidth} - ${position.left}) `;
                    }
                    else {
                        const nextEvent = nextOverlapedEvent(event, eventsArr, stack);
                        let sections;
                        if (nextEvent) {
                            sections = stack[nextEvent.id] - index;
                        }
                        else {
                            sections = maxOverlapCounts - index + 1;
                        }
                        position.width = `calc(${sectionWidth} * ${sections} - ${eventMargin}) `;
                        position.left = index
                            ? `calc(${sectionWidth} * ${index} + ${eventMargin})`
                            : "0%";
                    }
                    res.push({ position, eventData: event });
                });
            });
            return res;
        };
        this.getMultiDayEventsModel = (events, scaleModel) => {
            const res = [];
            const round = roundEvents(events);
            const crossedEvents = stackEvents(round);
            events.forEach(event => res.push({
                position: this.multieventPosition(event, scaleModel, crossedEvents),
                eventData: event,
            }));
            return res;
        };
        this.getViewModel = (activeDate, bounds, config, displayedEvents, calendars) => {
            this._config = config;
            const { hourScaleWidth, eventsOverlay, timeRange } = this._config;
            this._calendars = calendars;
            const timeScale = [];
            const now = new Date(activeDate);
            this._unsavedEvent = null;
            for (let i = timeRange[0]; i < timeRange[1]; i++) {
                now.setHours(i, 0);
                timeScale.push({
                    value: now.getHours() * 60 + now.getMinutes(),
                    label: new Date(now),
                });
            }
            const dateScale = [];
            let date = new Date(bounds[0]);
            while (date.valueOf() <= bounds[1].valueOf()) {
                dateScale.push({
                    value: date,
                });
                date = addDays(date, 1);
            }
            const model = {
                hourScaleWidth,
                timeScale,
                dateScale,
                calendars,
            };
            this._scaleModel = model;
            const events = [];
            const multievents = [];
            displayedEvents.forEach(e => {
                const end = addMinutes(e.end_date, -1);
                differenceInCalendarDays(e.start_date, end) === 0 &&
                    differenceInMinutes(e.end_date, e.start_date) < 24 * 60 - 1 &&
                    isEventInTimeRange(e, timeRange) &&
                    !e.allDay
                    ? events.push(e)
                    : multievents.push(e);
            });
            this._dayEvents = new EventStore();
            this._multievents = new EventStore();
            this._init = {
                dayEvents: this.getIndayEventsModel(events, model, {
                    overlay: eventsOverlay,
                }),
                multievents: this.getMultiDayEventsModel(multievents, model),
            };
            this._multieventDndDiff = null;
            this.resetToInit();
            return {
                ...model,
                title: this.getTitle,
                config: this._config,
                dayEvents: this._dayEvents,
                multievents: this._multievents,
                getMultieventsHeight: this.getTotalMultieventsHeight.bind(this),
                updateEventPosition: this.updateEventPosition.bind(this),
                getEventFromPosition: this.getEventFromPosition.bind(this),
                addEvent: this.addEvent.bind(this),
                removeEvent: this.removeEvent.bind(this),
                getEventById: this.getEventById.bind(this),
                resetToInit: this.resetToInit.bind(this),
                calculateMinutesLinePosition: this.calculateMinutesLinePosition.bind(this),
            };
        };
    }
    getEventFromPosition(id, trigger, e, locale) {
        let event;
        if (this._unsavedEvent && !this._config.autoSave) {
            this._multievents.remove(this._unsavedEvent.id);
            this._dayEvents.remove(this._unsavedEvent.id);
        }
        if (trigger === "data-cell") {
            const { timeStep } = this._config;
            const date = roundTimeByStep(updateDateByPos(new Date(), e, e.clientY), timeStep);
            const allDay = this.detectMultiGrid(e);
            const eventData = {
                text: locale("New Event"),
                start_date: date,
                end_date: date,
                id: uid(),
                allDay,
            };
            if (this._calendars.length) {
                eventData.type = this._calendars[0].id;
            }
            let position;
            if (allDay) {
                const round = roundEvents([
                    ...this._multievents.get().map(i => i.eventData),
                    eventData,
                ]);
                const crossedEvents = stackEvents(round);
                position = this.multieventPosition(eventData, this._scaleModel, crossedEvents);
            }
            else {
                position = this.calculateEventPosition(eventData, this._scaleModel);
            }
            event = {
                position,
                eventData,
            };
            this._unsavedEvent = eventData;
        }
        else if (trigger === "data-resizer") {
            const eventId = id;
            event =
                this._dayEvents
                    .get()
                    .filter(i => i.eventData.id == eventId)[0] ||
                    this._multievents
                        .get()
                        .filter(i => i.eventData.id == eventId)[0];
        }
        else {
            event =
                this._dayEvents.get().filter(i => i.eventData.id == id)[0] ||
                    this._multievents.get().filter(i => i.eventData.id == id)[0];
        }
        if (!this._dayEvents.includes(event.eventData) &&
            !this._multievents.includes(event.eventData)) {
            if (isMultiEvent(event.eventData)) {
                this._multievents.add(event);
            }
            else {
                this._dayEvents.add(event);
            }
        }
        if (isMultiEvent(event.eventData)) {
            const clickedDate = updateDateByPos(new Date(), e);
            const { start_date, end_date } = event.eventData;
            this._multieventDndDiff = {
                dndDiff: differenceInCalendarDays(start_date, clickedDate),
                eventDiff: differenceInMinutes(end_date, start_date),
            };
        }
        return event;
    }
    detectMultiGrid(e) {
        const elements = document.elementsFromPoint(e.clientX, e.clientY);
        for (let i = 0; i < elements.length; i++) {
            if (locateID(elements[i], "data-multievent"))
                return true;
        }
        return false;
    }
    addEvent(event) {
        if (this._dayEvents.includes(event) ||
            this._multievents.includes(event))
            return;
        if (this._unsavedEvent) {
            this._dayEvents.remove(this._unsavedEvent.id);
        }
        const newEvent = {
            position: this.calculateEventPosition(event, this._scaleModel),
            eventData: event,
        };
        this._dayEvents.add(newEvent);
        this._unsavedEvent = event;
    }
    removeEvent(id) {
        this._dayEvents.remove(id);
    }
    updateEventPosition(event, e, dragInfo, dragType) {
        const diff = { top: 0, bottom: 0, ...dragInfo };
        const { eventData } = event;
        const { top, bottom } = diff;
        const { timeStep } = this._config;
        const isMulti = isMultiEvent(eventData);
        const isMultiGrid = this.detectMultiGrid(e);
        if (dragType === "add") {
            const date = updateDateByPos(eventData.start_date, e, e.clientY);
            const dayDiff = differenceInCalendarDays(date, eventData.start_date);
            const end_date = roundTimeByStep(addDays(date, -dayDiff), timeStep);
            if (end_date > eventData.start_date) {
                eventData.end_date = end_date;
                event.position = {
                    ...this.calculateEventPosition(eventData, this._scaleModel),
                    zIndex: 10,
                };
                this._dayEvents.update(event);
            }
        }
        else {
            if (!isMultiGrid) {
                eventData.allDay = false;
                let minutesDiff = differenceInMinutes(eventData.end_date, eventData.start_date);
                if (isMulti) {
                    this._multievents.remove(event.eventData.id);
                    const diff = differenceInMinutes(eventData.end_date, eventData.start_date);
                    minutesDiff = diff < 24 * 60 ? diff : 120;
                }
                if (dragType === "resize") {
                    document.body.style.cursor = "n-resize";
                }
                if (dragType === "update") {
                    const newDate = updateDateByPos(eventData.start_date, e, e.clientY - top);
                    const new_start_date = roundTimeByStep(newDate, timeStep);
                    const new_end_date = addMinutes(new_start_date, minutesDiff);
                    if (!isSameDay(new_start_date, eventData.start_date) &&
                        !isSameDay(new_start_date, new_end_date))
                        return;
                    eventData.start_date = new_start_date;
                    eventData.end_date = new_end_date;
                }
                else {
                    let newDate = updateDateByPos(eventData.end_date, e, e.clientY + bottom);
                    const dayDiff = differenceInCalendarDays(newDate, eventData.start_date);
                    newDate = addDays(newDate, -dayDiff);
                    if (newDate > eventData.start_date) {
                        eventData.end_date = roundTimeByStep(newDate, timeStep);
                    }
                }
                const position = this.calculateEventPosition(eventData, this._scaleModel);
                event.position = {
                    ...position,
                    zIndex: 10,
                };
                this._dayEvents.update(event);
            }
            else {
                if (!isMulti) {
                    this._dayEvents.remove(event.eventData.id);
                    if (!this._multieventDndDiff) {
                        this._multieventDndDiff = { dndDiff: 0 };
                    }
                }
                if (dragType === "resize") {
                    document.body.style.cursor = "w-resize";
                }
                const { eventDiff } = this._multieventDndDiff;
                const minutesDif = eventDiff ||
                    differenceInMinutes(eventData.end_date, eventData.start_date);
                if (isMulti) {
                    if (dragType === "update") {
                        const new_date = addDays(updateDateByPos(eventData.start_date, e), this._multieventDndDiff.dndDiff || 0);
                        if (!isSameDay(new_date, eventData.start_date)) {
                            eventData.start_date = new_date;
                            const h = getHours(eventData.end_date);
                            const m = getMinutes(eventData.end_date);
                            eventData.end_date = addMinutes(eventData.start_date, minutesDif);
                            eventData.end_date.setHours(h, m);
                        }
                    }
                }
                if (dragType === "resize") {
                    const start = startOfDay(eventData.end_date);
                    const end = updateDateByPos(eventData.end_date, e);
                    // add 1 day if end_date was 00:00
                    eventData.end_date = !differenceInMinutes(start, eventData.end_date)
                        ? addDays(end, 1)
                        : end;
                }
                if (isSameDay(eventData.start_date, eventData.end_date)) {
                    eventData.allDay = true;
                }
                this._multievents.update(event);
                const round = roundEvents(this._multievents.get().map(i => i.eventData));
                const crossedEvents = stackEvents(round);
                this._multievents.set(this._multievents.get().map(ev => ({
                    ...ev,
                    position: this.multieventPosition(ev.eventData, this._scaleModel, crossedEvents),
                })));
            }
        }
    }
    getTotalMultieventsHeight(events) {
        if (events.length) {
            const round = roundEvents(events.map(ev => ev.eventData));
            const crossedEvents = Object.values(stackEvents(round));
            const maxIndex = Math.max(...crossedEvents);
            this._multieventHeight = `${(maxIndex + 1) * this._config.eventHeight + maxIndex * 5}px`;
        }
        else {
            this._multieventHeight = "0px";
        }
        return this._multieventHeight;
    }
    getEventById(id) {
        return this._dayEvents.getEvent(id) || this._multievents.getEvent(id);
    }
    resetToInit() {
        this._dayEvents.set(this._init.dayEvents.map(ev => ({
            position: { ...ev.position },
            eventData: { ...ev.eventData },
        })));
        this._multievents.set(this._init.multievents.map(ev => ({
            position: { ...ev.position },
            eventData: { ...ev.eventData },
        })));
    }
    calculateMinutesLinePosition() {
        const { timeScale } = this._scaleModel;
        const now = new Date().valueOf();
        const start = startOfHour(timeScale[0].label).valueOf();
        const end = endOfHour(timeScale[timeScale.length - 1].label).valueOf();
        if (now < start || now > end)
            return;
        const endScale = end - start;
        return `${((now - start) * 100) / endScale}%`;
    }
}
export default WeekView;
//# sourceMappingURL=week.js.map